import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@/hooks/useApiClient';

export default function AdminBillingPage() {
  const { t } = useTranslation();
  const apiClient = useApiClient();

  const billingQuery = useQuery({
    queryKey: ['admin:billing'],
    queryFn: () => apiClient.getBillingDashboard(),
  });

  const subscriptionsQuery = useQuery({
    queryKey: ['admin:subscriptions-for-billing'],
    queryFn: () => apiClient.getAdminSubscriptions(0, 100),
  });

  const data = billingQuery.data;
  const subscriptions = subscriptionsQuery.data?.data || [];

  const activeSubscriptions = subscriptions.filter((s: any) => s.status === 'ACTIVE').length;
  const trialSubscriptions = subscriptions.filter((s: any) => s.status === 'TRIALING').length;
  const pastDueSubscriptions = subscriptions.filter((s: any) => s.status === 'PAST_DUE').length;

  const isLoading = billingQuery.isLoading || subscriptionsQuery.isLoading;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900">📊 {t('admin.billingTitle')}</h1>
        <p className="text-neutral-600 mt-2">{t('admin.revenueAndStats')}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">{t('admin.totalRevenue')}</p>
              <p className="text-3xl font-bold mt-2">
                {isLoading ? '...' : `${((data?.totalRevenue || 0) / 100).toFixed(2)} €`}
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <span className="text-2xl">💰</span>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">{t('admin.mrrFull')}</p>
              <p className="text-3xl font-bold mt-2">
                {isLoading ? '...' : `${((data?.monthlyRecurringRevenue || 0) / 100).toFixed(2)} €`}
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <span className="text-2xl">📈</span>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-100 text-sm font-medium">{t('admin.arrFull')}</p>
              <p className="text-3xl font-bold mt-2">
                {isLoading ? '...' : `${((data?.monthlyRecurringRevenue || 0) * 12 / 100).toFixed(2)} €`}
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <span className="text-2xl">🎯</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm">
          <div className="px-6 py-4 border-b border-neutral-200">
            <h2 className="font-bold text-neutral-900">💳 {t('admin.subscriptionMetrics')}</h2>
          </div>
          <div className="p-6">
            {isLoading ? (
              <div className="text-center py-4 sm:py-8 text-neutral-500">{t('common.loading')}</div>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-3 border-b border-neutral-100">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-neutral-700">{t('admin.activeSubscriptions')}</span>
                  </div>
                  <span className="font-bold text-neutral-900 text-lg">{activeSubscriptions}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-3 border-b border-neutral-100">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span className="text-neutral-700">{t('admin.trial')}</span>
                  </div>
                  <span className="font-bold text-neutral-900 text-lg">{trialSubscriptions}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-3 border-b border-neutral-100">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                    <span className="text-neutral-700">{t('admin.pastDue')}</span>
                  </div>
                  <span className="font-bold text-orange-600 text-lg">{pastDueSubscriptions}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-3">
                  <span className="text-neutral-700 font-medium">{t('admin.totalSubscriptions')}</span>
                  <span className="font-bold text-neutral-900 text-lg">{data?.activeSubscriptions || subscriptions.length}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm">
          <div className="px-6 py-4 border-b border-neutral-200">
            <h2 className="font-bold text-neutral-900">📊 {t('admin.platformStats')}</h2>
          </div>
          <div className="p-6">
            {isLoading ? (
              <div className="text-center py-4 sm:py-8 text-neutral-500">{t('common.loading')}</div>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-3 border-b border-neutral-100">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">🏢</span>
                    <span className="text-neutral-700">{t('admin.totalEstablishments')}</span>
                  </div>
                  <span className="font-bold text-neutral-900 text-lg">{data?.totalEstablishments || 0}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-3 border-b border-neutral-100">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">👥</span>
                    <span className="text-neutral-700">{t('admin.totalUsers')}</span>
                  </div>
                  <span className="font-bold text-neutral-900 text-lg">{data?.totalUsers || 0}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-3 border-b border-neutral-100">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">📦</span>
                    <span className="text-neutral-700">{t('admin.totalDataPoints')}</span>
                  </div>
                  <span className="font-bold text-neutral-900 text-lg">{data?.totalDataPoints || 0}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-3">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">📈</span>
                    <span className="text-neutral-700">{t('admin.conversionRate')}</span>
                  </div>
                  <span className="font-bold text-green-600 text-lg">
                    {trialSubscriptions > 0
                      ? `${((activeSubscriptions / (activeSubscriptions + trialSubscriptions)) * 100).toFixed(1)}%`
                      : '-'}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-neutral-200 shadow-sm">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h2 className="font-bold text-neutral-900">💵 {t('admin.financialSummary')}</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-green-600 font-medium">{t('admin.totalRevenue')}</p>
              <p className="text-2xl font-bold text-green-700 mt-2">
                {isLoading ? '...' : `${((data?.totalRevenue || 0) / 100).toFixed(2)} €`}
              </p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-600 font-medium">{t('admin.mrr')}</p>
              <p className="text-2xl font-bold text-blue-700 mt-2">
                {isLoading ? '...' : `${((data?.monthlyRecurringRevenue || 0) / 100).toFixed(2)} €`}
              </p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-purple-600 font-medium">{t('admin.arpu')}</p>
              <p className="text-2xl font-bold text-purple-700 mt-2">
                {isLoading ? '...' : activeSubscriptions > 0
                  ? `${((data?.monthlyRecurringRevenue || 0) / 100 / activeSubscriptions).toFixed(2)} €`
                  : '0 €'}
              </p>
              <p className="text-xs text-purple-500 mt-1">{t('admin.perActiveUser')}</p>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-lg">
              <p className="text-sm text-amber-600 font-medium">{t('admin.annualProjection')}</p>
              <p className="text-2xl font-bold text-amber-700 mt-2">
                {isLoading ? '...' : `${((data?.monthlyRecurringRevenue || 0) * 12 / 100).toFixed(2)} €`}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
